<template>
  <div class="storyboard-step">
    <!-- 宫格大图生成区 -->
    <el-card shadow="never" class="storyboard-card">
      <template #header>
        <div class="card-header">
          <div class="header-title">
            <span>宫格大图生成</span>
            <el-tag v-if="gridCalcResult" type="info" size="small">
              推荐 {{ gridCalcResult.recommended_grid_size }} = {{ gridCalcResult.recommended_grid_count }}宫格
            </el-tag>
          </div>
          <el-button 
            type="primary" 
            @click="openPreviewDialog" 
            :loading="loadingPreview"
            :disabled="!canGenerate"
          >
            <el-icon><Grid /></el-icon>
            生成分镜大图
          </el-button>
        </div>
      </template>

      <el-alert
        v-if="!hasCollages"
        title="请先完成设定图合并"
        description="需要先在设定图步骤中合并各类设定图，才能生成分镜大图"
        type="warning"
        show-icon
        :closable="false"
        class="mb-4"
      >
        <template #default>
          <p>请前往"设定图"步骤，点击"一键合并全部设定图"按钮，将人物、地点、道具、世界观设定图合并后即可生成分镜。</p>
        </template>
      </el-alert>

      <el-alert
        v-if="hasCollages && noScripts"
        title="请先完成剧本拆解步骤"
        description="需要先在剧本拆解步骤中生成场景片段，才能计算宫格规格"
        type="warning"
        show-icon
        :closable="false"
        class="mb-4"
      />

      <!-- 宫格计算详情 -->
      <div v-if="hasCollages && !noScripts && gridCalcResult && !storyboards.length" class="grid-calc-section mb-4">
        <el-collapse v-model="calcCollapseActive">
          <el-collapse-item title="宫格计算详情" name="detail">
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item label="场景数">{{ gridCalcResult.scenes.length }}</el-descriptions-item>
              <el-descriptions-item label="场景切换">{{ gridCalcResult.transition_count }}次 × {{ gridCalcResult.transition_duration }}秒</el-descriptions-item>
              <el-descriptions-item label="总时长">{{ gridCalcResult.total_duration }}秒</el-descriptions-item>
              <el-descriptions-item label="原始宫格数">{{ gridCalcResult.raw_grid_count }}</el-descriptions-item>
              <el-descriptions-item label="推荐规格">
                <el-tag type="success">{{ gridCalcResult.recommended_grid_size }}</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="填充Panel">{{ gridCalcResult.filler_count }}个</el-descriptions-item>
            </el-descriptions>

            <el-table :data="gridCalcResult.scenes" size="small" class="mt-4" border>
              <el-table-column prop="title" label="场景名称" min-width="120" />
              <el-table-column label="时长" width="70" align="center">
                <template #default="{ row }">{{ row.panel_count }}秒</template>
              </el-table-column>
              <el-table-column label="台词" width="70" align="center">
                <template #default="{ row }">
                  <el-tag :type="row.has_dialogue ? 'danger' : 'info'" size="small">
                    {{ row.has_dialogue ? '有' : '无' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Panel数" width="80" align="center">
                <template #default="{ row }">{{ row.panel_count }}</template>
              </el-table-column>
              <el-table-column label="Panel范围" width="110" align="center">
                <template #default="{ row }">{{ row.panel_range[0] }}-{{ row.panel_range[1] }}</template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>

        <!-- 宫格规格选择 -->
        <div class="grid-size-selector mt-4">
          <el-form :inline="true">
            <el-form-item label="宫格规格">
              <el-select v-model="selectedGridSize" placeholder="选择宫格规格">
                <el-option
                  v-for="n in 9"
                  :key="n"
                  :label="`${n + 1}x${n + 1} = ${(n + 1) * (n + 1)}宫格`"
                  :value="`${n + 1}x${n + 1}`"
                />
              </el-select>
            </el-form-item>
          </el-form>
          <el-alert
            v-if="isGridSizeTooSmall"
            title="宫格数不足"
            description="选择的宫格规格小于推荐值，部分场景可能被截断"
            type="warning"
            show-icon
            :closable="false"
          />
        </div>
      </div>

      <div v-if="hasCollages && !noScripts && !storyboards.length && !gridCalcResult" class="empty-state">
        <el-empty description="暂无宫格大图">
          <p>点击"生成分镜大图"按钮生成宫格分镜图</p>
        </el-empty>
      </div>

      <div v-if="storyboards.length" class="storyboards-list">
        <el-row :gutter="16">
          <el-col :span="8" v-for="storyboard in storyboards" :key="storyboard.id">
            <el-card shadow="hover" class="storyboard-item" :class="{ 'is-default': storyboard.is_default }">
              <div class="storyboard-image" @click="previewImage(storyboard)">
                <el-image 
                  :src="storyboard.image_url" 
                  fit="cover"
                  :preview-src-list="[storyboard.image_url]"
                />
                <div v-if="storyboard.is_default" class="default-badge">
                  <el-icon><Check /></el-icon> 默认
                </div>
              </div>
              <div class="storyboard-info">
                <p class="storyboard-meta">
                  {{ storyboard.grid_size }} · {{ storyboard.grid_count }}宫格
                </p>
                <p class="storyboard-time">{{ formatTime(storyboard.created_at) }}</p>
              </div>
              <div class="storyboard-actions">
                <el-button 
                  v-if="!storyboard.is_default" 
                  link 
                  @click="setDefaultStoryboard(storyboard.id)"
                >
                  设为默认
                </el-button>
                <el-button 
                  type="primary" 
                  size="small"
                  @click="splitStoryboard(storyboard)"
                  :loading="splittingId === storyboard.id"
                >
                  <el-icon><Crop /></el-icon>
                  拆分宫格
                </el-button>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </el-card>

    <!-- 三栏布局：宫格小图 | 扩图分镜 | 视频素材 -->
    <div v-if="cells.length || shots.length || videoSegments.length" class="three-column-layout">
      <!-- 左栏：宫格小图 -->
      <el-card v-if="cells.length" shadow="never" class="column-card cells-column">
        <template #header>
          <div class="card-header">
            <span>宫格小图 ({{ cells.length }})</span>
            <el-button type="primary" size="small" @click="showOutpaintDialog">
              <el-icon><FullScreen /></el-icon>
              批量扩图
            </el-button>
          </div>
        </template>
        <div class="cells-grid">
          <div class="cell-item" v-for="cell in cells" :key="cell.id" @click="previewImage(cell)">
            <el-image :src="cell.outpainted_image_url || cell.image_url" fit="cover" />
            <div class="cell-index">{{ cell.cell_index }}</div>
            <div v-if="cell.is_expanded" class="expanded-badge">已扩图</div>
            <div v-if="outpaintingCellIds.has(cell.id)" class="outpainting-badge">扩图中</div>
            <div class="cell-actions" @click.stop>
              <el-button
                v-if="!cell.is_expanded && !outpaintingCellIds.has(cell.id)"
                size="small"
                type="primary"
                link
                @click="outpaintSingleCell(cell)"
              >
                扩图
              </el-button>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 中栏：扩图后的分镜画面 -->
      <el-card v-if="shots.length" shadow="never" class="column-card shots-column">
        <template #header>
          <div class="card-header">
            <span>分镜画面 ({{ shots.length }})</span>
            <el-button type="primary" size="small" @click="generateVideos" :loading="generatingVideos">
              <el-icon><VideoCamera /></el-icon>
              生成视频
            </el-button>
          </div>
        </template>
        <div class="shots-grid">
          <div class="shot-item" v-for="shot in shots" :key="shot.id">
            <div class="shot-image" @click="previewImage(shot)">
              <el-image 
                :src="shot.outpainted_image_url || shot.image_url" 
                fit="cover"
              />
              <div v-if="shot.outpainted_image_url" class="outpaint-badge">已扩图</div>
            </div>
            <p class="shot-index">镜头 {{ shot.shot_index }}</p>
          </div>
        </div>
      </el-card>

      <!-- 右栏：视频素材 -->
      <el-card v-if="videoSegments.length" shadow="never" class="column-card videos-column">
        <template #header>
          <div class="card-header">
            <span>视频素材 ({{ videoSegments.length }})</span>
            <el-button type="success" size="small" @click="mergeVideos" :loading="merging">
              <el-icon><VideoPlay /></el-icon>
              合并视频
            </el-button>
          </div>
        </template>
        <div class="videos-grid">
          <div class="video-item" v-for="segment in videoSegments" :key="segment.id">
            <div class="video-preview">
              <el-image :src="segment.image_url" fit="cover" />
              <div v-if="segment.video_url" class="video-play-icon">
                <el-icon><VideoPlay /></el-icon>
              </div>
              <div v-else class="video-status">{{ segment.status }}</div>
            </div>
            <p class="video-info">片段 {{ segment.segment_order }} · {{ segment.duration }}秒</p>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 生成前确认对话框 -->
    <el-dialog
      v-model="confirmDialogVisible"
      title="生成宫格大图 - 确认信息"
      width="85%"
      :close-on-click-modal="false"
      :close-on-press-escape="generationStatus === 'idle' || generationStatus === 'completed' || generationStatus === 'failed'"
      :before-close="handleDialogClose"
      top="3vh"
      class="confirm-dialog"
    >
      <el-alert
        title="请仔细检查以下内容，特别是提示词中是否包含敏感词（暴力、恐怖、色情等），您可以编辑修改后再提交"
        type="warning"
        show-icon
        :closable="false"
        class="mb-4"
      />

      <el-tabs v-model="confirmActiveTab">
        <!-- Tab 1: 基本信息 -->
        <el-tab-pane label="基本信息" name="basic">
          <el-descriptions :column="3" border size="small" class="mb-4">
            <el-descriptions-item label="宫格图数">
              <el-tag type="success" size="large">{{ previewData.total_grids || 1 }}张</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="总场景数">{{ previewData.scenes?.length || 0 }}</el-descriptions-item>
            <el-descriptions-item label="总时长">{{ previewData.total_duration }}秒</el-descriptions-item>
          </el-descriptions>

          <el-table v-if="previewData.grids?.length" :data="previewData.grids" size="small" border class="mb-4">
            <el-table-column label="#" width="50" align="center">
              <template #default="{ $index }">{{ $index + 1 }}</template>
            </el-table-column>
            <el-table-column label="规格" width="80" align="center" prop="grid_size" />
            <el-table-column label="宫格数" width="70" align="center" prop="grid_count" />
            <el-table-column label="场景数" width="70" align="center" prop="scene_count" />
            <el-table-column label="Panel数" width="70" align="center" prop="panel_count" />
            <el-table-column label="填充" width="60" align="center" prop="filler_count" />
            <el-table-column label="包含场景" min-width="150">
              <template #default="{ row }">
                {{ row.scenes.map(s => s.title || '未命名').join(' → ') }}
              </template>
            </el-table-column>
          </el-table>

          <div v-if="previewData.reference_images?.length" class="ref-images-section mb-4">
            <h4>参考设定图 ({{ previewData.reference_images.length }}张)</h4>
            <div class="ref-images-grid">
              <div v-for="ref in previewData.reference_images" :key="ref.type" class="ref-image-item">
                <el-image :src="ref.url" fit="cover" />
                <span class="ref-type">{{ ref.type }}</span>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- Tab 2: 场景内容（可编辑） -->
        <el-tab-pane label="场景内容（可编辑）" name="scenes">
          <el-alert
            title="修改场景内容后，提示词将自动重新生成。请检查并移除可能的敏感词。"
            type="info"
            show-icon
            :closable="false"
            class="mb-4"
          />
          <div v-for="(scene, idx) in editableScenes" :key="idx" class="scene-edit-block">
            <div class="scene-edit-header">
              <el-tag :type="scene.has_dialogue ? 'danger' : 'info'" size="small">
                {{ scene.has_dialogue ? '有台词' : '无台词' }}
              </el-tag>
              <span class="scene-edit-title">场景 {{ idx + 1 }}: {{ scene.title }}</span>
              <span class="scene-edit-meta">Panel {{ scene.panel_range?.[0] }}-{{ scene.panel_range?.[1] }} · {{ scene.panel_count }}秒</span>
            </div>
            <el-form label-width="80px" size="small">
              <el-form-item label="场景标题">
                <el-input v-model="scene.title" placeholder="场景标题" />
              </el-form-item>
              <el-form-item label="地点">
                <el-input v-model="scene.location" placeholder="场景地点" />
              </el-form-item>
              <el-form-item label="角色">
                <el-input v-model="scene.charactersText" placeholder="角色名称，用逗号分隔" />
              </el-form-item>
              <el-form-item v-if="scene.has_dialogue" label="台词内容">
                <el-input
                  v-model="scene.dialogue_content"
                  type="textarea"
                  :rows="3"
                  placeholder="台词内容，请检查敏感词"
                />
                <div v-if="scene.voices && scene.voices.length" class="voice-players">
                  <div v-for="(voice, vi) in scene.voices" :key="vi" class="voice-item">
                    <span class="voice-speaker">{{ voice.character_name || voice.speaker || '角色' }}</span>
                    <el-tag size="small" type="info" class="voice-speaker-tag">{{ voice.speaker }}</el-tag>
                    <audio :src="voice.url" controls class="voice-audio" />
                    <span class="voice-duration">{{ voice.duration }}秒</span>
                  </div>
                </div>
              </el-form-item>
              <el-form-item label="视觉风格">
                <el-input v-model="scene.visual_style" placeholder="视觉风格描述" />
              </el-form-item>
              <el-form-item label="场景描述">
                <el-input
                  v-model="scene.description"
                  type="textarea"
                  :rows="2"
                  placeholder="场景描述，请检查敏感词"
                />
              </el-form-item>
            </el-form>
          </div>
          <div class="mt-4" style="text-align: center;">
            <el-button type="primary" @click="regeneratePromptFromScenes" :loading="regeneratingPrompt">
              根据修改后的场景内容重新生成提示词
            </el-button>
          </div>
        </el-tab-pane>

        <!-- Tab 3: 提示词（可编辑，按宫格图分别显示） -->
        <el-tab-pane label="提示词（可编辑）" name="prompt">
          <el-alert
            :title="`每张宫格图独立提示词，${selectedEngine === 'wanxiang' ? '万相2.7支持5000字符' : '即梦API限制800字符'}。请检查并移除敏感词后再提交`"
            type="warning"
            show-icon
            :closable="false"
            class="mb-4"
          />
          <div v-if="editableGridPrompts.length === 1" class="single-prompt">
            <el-form label-position="top">
              <el-form-item>
                <el-input
                  v-model="editableGridPrompts[0].prompt"
                  type="textarea"
                  :rows="16"
                  class="prompt-editor"
                />
              </el-form-item>
            </el-form>
            <div class="prompt-stats">
              <span :style="{ color: editableGridPrompts[0].prompt.length > promptCharLimit ? '#f56c6c' : editableGridPrompts[0].prompt.length > promptCharLimit * 0.75 ? '#e6a23c' : '#67c23a' }">
                字符数: {{ editableGridPrompts[0].prompt.length }} / {{ promptCharLimit }}
              </span>
              <el-tag v-if="editableGridPrompts[0].prompt.length > promptCharLimit" type="danger" size="small">超出限制！</el-tag>
            </div>
          </div>
          <div v-else class="multi-prompt">
            <el-collapse v-model="gridPromptCollapseActive">
              <el-collapse-item
                v-for="(gp, gi) in editableGridPrompts"
                :key="gi"
                :title="`宫格图 ${gi + 1}: ${gp.grid_size} = ${gp.grid_count}宫格 (${gp.scene_count}场景, ${gp.panel_count}Panel)`"
                :name="gi"
              >
                <el-form label-position="top">
                  <el-form-item>
                    <el-input
                      v-model="gp.prompt"
                      type="textarea"
                      :rows="10"
                      class="prompt-editor"
                    />
                  </el-form-item>
                </el-form>
                <div class="prompt-stats">
                  <span :style="{ color: gp.prompt.length > promptCharLimit ? '#f56c6c' : gp.prompt.length > promptCharLimit * 0.75 ? '#e6a23c' : '#67c23a' }">
                    字符数: {{ gp.prompt.length }} / {{ promptCharLimit }}
                  </span>
                  <el-tag v-if="gp.prompt.length > promptCharLimit" type="danger" size="small">超出限制！</el-tag>
                </div>
              </el-collapse-item>
            </el-collapse>
          </div>
        </el-tab-pane>
      </el-tabs>

      <template #footer>
        <div class="confirm-dialog-footer">
          <div v-if="generationStatus === 'idle'" class="engine-selector mb-4">
            <span class="engine-label">生图引擎：</span>
            <el-radio-group v-model="selectedEngine" size="small">
              <el-radio-button value="jimeng">
                即梦4.0
                <el-tag size="small" type="warning" class="ml-1">800字</el-tag>
              </el-radio-button>
              <el-radio-button value="wanxiang" :disabled="!wanxiangConfigured">
                万相2.7
                <el-tag size="small" type="success" class="ml-1">5000字</el-tag>
              </el-radio-button>
            </el-radio-group>
            <el-tag v-if="selectedEngine === 'wanxiang' && !wanxiangConfigured" type="danger" size="small" class="ml-2">
              未配置API Key
            </el-tag>
          </div>
          <div v-if="generating || generationStatus !== 'idle'" class="generation-progress">
            <el-progress 
              :percentage="generationProgress" 
              :status="generationStatus === 'completed' ? 'success' : generationStatus === 'failed' ? 'exception' : ''"
              :stroke-width="20"
              :text-inside="true"
              striped
              striped-flow
            />
            <p class="progress-text">
              <template v-if="generationStatus === 'submitting'">
                正在提交第 {{ completedGrids + 1 }}/{{ totalGrids }} 张宫格图...
              </template>
              <template v-else-if="generationStatus === 'generating'">
                第 {{ completedGrids + 1 }}/{{ totalGrids }} 张宫格图生成中...
              </template>
              <template v-else-if="generationStatus === 'completed'">全部生成完成！</template>
              <template v-else-if="generationStatus === 'failed'">部分生成失败</template>
            </p>
          </div>
          <div class="footer-buttons">
            <el-button @click="handleDialogClose" :disabled="generationStatus === 'submitting'">关闭</el-button>
            <el-button 
              v-if="generationStatus === 'idle'" 
              type="primary" 
              @click="confirmAndGenerate" 
              :loading="generating" 
              size="large"
            >
              <el-icon><Grid /></el-icon>
              确认并生成宫格大图
            </el-button>
            <el-button 
              v-else-if="generationStatus === 'completed'" 
              type="success" 
              @click="closeAndRefresh" 
              size="large"
            >
              查看生成结果
            </el-button>
            <el-button 
              v-else-if="generationStatus === 'failed'" 
              type="warning" 
              @click="closeAndRefresh" 
              size="large"
            >
              关闭并查看结果
            </el-button>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 扩图对话框 -->
    <el-dialog v-model="outpaintDialogVisible" title="批量扩图" width="400px">
      <el-form :model="outpaintForm" label-width="100px">
        <el-form-item label="目标比例">
          <el-select v-model="outpaintForm.aspectRatio">
            <el-option label="16:9 (横屏)" value="16:9" />
            <el-option label="9:16 (竖屏)" value="9:16" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="outpaintDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="startOutpaint" :loading="outpainting">
          开始扩图
        </el-button>
      </template>
    </el-dialog>

    <!-- 图片预览 -->
    <el-dialog v-model="previewVisible" title="图片预览" width="80%" destroy-on-close class="preview-dialog">
      <div class="preview-image-container">
        <img :src="previewImageUrl" v-if="previewImageUrl" class="preview-full-image" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Grid, Crop, Check, FullScreen, VideoCamera, VideoPlay, Picture } from '@element-plus/icons-vue'
import api from '../../api/index.js'

const props = defineProps({
  projectId: {
    type: Number,
    required: true
  },
  scriptId: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['step-complete', 'step-save'])

const generating = ref(false)
const loadingPreview = ref(false)
const regeneratingPrompt = ref(false)
const splittingId = ref(null)
const outpainting = ref(false)
const generatingVideos = ref(false)
const merging = ref(false)
const hasCollages = ref(false)
const noScripts = ref(false)
const gridCalcResult = ref(null)
const selectedGridSize = ref('auto')
const calcCollapseActive = ref([])
const pollingTimer = ref(null)
const outpaintPollingTimers = ref([])

const storyboards = ref([])
const cells = ref([])
const shots = ref([])
const videoSegments = ref([])
const outpaintingCellIds = ref(new Set())

const outpaintDialogVisible = ref(false)
const outpaintForm = ref({
  aspectRatio: '16:9'
})

const previewVisible = ref(false)
const previewImageUrl = ref('')

const confirmDialogVisible = ref(false)
const confirmActiveTab = ref('basic')
const previewData = ref({})
const editablePrompt = ref('')
const editableScenes = ref([])
const editableGridPrompts = ref([])
const gridPromptCollapseActive = ref([0])
const selectedEngine = ref('jimeng')
const wanxiangConfigured = ref(false)
const promptCharLimit = computed(() => selectedEngine.value === 'wanxiang' ? 5000 : 800)
const generationStatus = ref('idle')
const generationProgress = ref(0)
const completedGrids = ref(0)
const totalGrids = ref(0)
const pollingStoryboardIds = ref([])
const generationCancelled = ref(false)

const isGridSizeTooSmall = computed(() => {
  if (!gridCalcResult.value || selectedGridSize.value === 'auto') return false
  const selVal = parseInt(selectedGridSize.value.split('x')[0])
  const recVal = parseInt(gridCalcResult.value.recommended_grid_size.split('x')[0])
  return selVal < recVal
})

const canGenerate = computed(() => {
  return hasCollages.value && !generating.value && !noScripts.value
})

const checkCollages = async () => {
  try {
    const data = await api.get(`/projects/${props.projectId}/setting-collages`)
    if (data.success && data.data) {
      const collageKeys = Object.keys(data.data)
      hasCollages.value = collageKeys.length > 0
    } else {
      hasCollages.value = false
    }
  } catch (error) {
    console.error('检查设定合并图失败:', error)
    hasCollages.value = false
  }
}

const loadGridCalc = async () => {
  try {
    const data = await api.get(`/projects/${props.projectId}/storyboard-grid-calc`)
    if (data.success && data.data) {
      gridCalcResult.value = data.data
      selectedGridSize.value = data.data.recommended_grid_size
      noScripts.value = false
    } else if (!data.success && data.message && data.message.includes('剧本拆解')) {
      noScripts.value = true
      gridCalcResult.value = null
    }
  } catch (error) {
    console.error('加载宫格计算结果失败:', error)
    noScripts.value = true
    gridCalcResult.value = null
  }
}

const openPreviewDialog = async () => {
  loadingPreview.value = true
  try {
    // 检查万相API配置
    try {
      const statusResp = await api.get('/system/status')
      wanxiangConfigured.value = !!(statusResp.api_configured?.wanxiang)
      if (statusResp.default_image_engine && wanxiangConfigured.value) {
        selectedEngine.value = statusResp.default_image_engine
      }
    } catch (e) {
      wanxiangConfigured.value = false
    }

    const gridSizeToSend = selectedGridSize.value || 'auto'
    const data = await api.post(`/projects/${props.projectId}/storyboard-prompt-preview`, {
      grid_size: gridSizeToSend,
      engine: selectedEngine.value
    })

    if (data.success && data.data) {
      previewData.value = data.data
      editablePrompt.value = data.data.prompt || ''

      editableScenes.value = (data.data.scenes || []).map(scene => ({
        ...scene,
        charactersText: Array.isArray(scene.characters) ? scene.characters.join(', ') : (scene.characters || ''),
      }))

      if (data.data.grid_prompts && data.data.grid_prompts.length > 0) {
        editableGridPrompts.value = data.data.grid_prompts
        gridPromptCollapseActive.value = data.data.grid_prompts.map((_, i) => i)
      } else {
        editableGridPrompts.value = [{
          index: 0,
          grid_size: data.data.grid_size || '4x4',
          grid_count: data.data.grid_count || 16,
          scene_count: data.data.scenes?.length || 0,
          panel_count: data.data.raw_grid_count || 0,
          prompt: data.data.prompt || '',
          prompt_length: (data.data.prompt || '').length,
        }]
      }

      confirmActiveTab.value = 'basic'
      confirmDialogVisible.value = true
    } else {
      ElMessage.error(data.message || '获取预览信息失败')
    }
  } catch (error) {
    ElMessage.error('获取预览信息失败: ' + error.message)
  } finally {
    loadingPreview.value = false
  }
}

const regeneratePromptFromScenes = async () => {
  regeneratingPrompt.value = true
  try {
    const updatedScenes = editableScenes.value.map(scene => ({
      ...scene,
      characters: scene.charactersText ? scene.charactersText.split(/[,，]/).map(s => s.trim()).filter(Boolean) : [],
    }))

    const gridSizeToSend = selectedGridSize.value || previewData.value.grid_size || 'auto'
    const data = await api.post(`/projects/${props.projectId}/storyboard-prompt-preview`, {
      grid_size: gridSizeToSend,
      scenes_override: updatedScenes
    })

    if (data.success && data.data) {
      editablePrompt.value = data.data.prompt || ''
      if (data.data.grid_prompts && data.data.grid_prompts.length > 0) {
        editableGridPrompts.value = data.data.grid_prompts
      }
      ElMessage.success('提示词已根据修改后的场景内容重新生成')
    } else {
      ElMessage.error(data.message || '重新生成提示词失败')
    }
  } catch (error) {
    ElMessage.error('重新生成提示词失败: ' + error.message)
  } finally {
    regeneratingPrompt.value = false
  }
}

const confirmAndGenerate = async () => {
  const maxChars = selectedEngine.value === 'wanxiang' ? 5000 : 800
  const hasOverLimit = editableGridPrompts.value.some(gp => gp.prompt.length > maxChars)
  if (hasOverLimit) {
    ElMessage.warning(`有提示词超过${maxChars}字符限制，请先修改`)
    return
  }

  for (const gp of editableGridPrompts.value) {
    if (!gp.prompt.trim()) {
      ElMessage.warning('有提示词为空，请检查')
      return
    }
  }

  totalGrids.value = editableGridPrompts.value.length
  completedGrids.value = 0
  generationStatus.value = 'submitting'
  generationProgress.value = 0
  generating.value = true
  generationCancelled.value = false
  let hasFailure = false

  try {
    for (let gi = 0; gi < editableGridPrompts.value.length; gi++) {
      if (generationCancelled.value) break
      const gp = editableGridPrompts.value[gi]
      generationStatus.value = 'submitting'
      generationProgress.value = Math.round((completedGrids.value / totalGrids.value) * 100)

      const gridPromptToSend = [{
        grid_size: gp.grid_size,
        prompt: gp.prompt,
        scenes: (previewData.value.grids?.[gp.index]?.scenes) || [],
      }]

      let submitSuccess = false
      let storyboardId = null
      let taskId = null

      try {
        const data = await api.post(`/projects/${props.projectId}/storyboards`, {
          script_id: props.scriptId,
          grid_prompts: gridPromptToSend,
          engine: selectedEngine.value,
        })

        if (data.success) {
          const results = data.data || []
          const item = results[0]
          if (item) {
            if (item.status === 'completed') {
              completedGrids.value++
              generationProgress.value = Math.round((completedGrids.value / totalGrids.value) * 100)
              submitSuccess = true
              continue
            } else if (item.status === 'generating') {
              storyboardId = item.storyboard_id
              taskId = item.task_id
              submitSuccess = true
            } else if (item.status === 'failed') {
              hasFailure = true
              completedGrids.value++
              generationProgress.value = Math.round((completedGrids.value / totalGrids.value) * 100)
              ElMessage.error(`宫格图${gi + 1}提交失败: ${item.error || '未知错误'}`)
              continue
            }
          }
        } else {
          hasFailure = true
          completedGrids.value++
          generationProgress.value = Math.round((completedGrids.value / totalGrids.value) * 100)
          ElMessage.error(`宫格图${gi + 1}提交失败: ${data.message || '未知错误'}`)
          continue
        }
      } catch (error) {
        hasFailure = true
        completedGrids.value++
        generationProgress.value = Math.round((completedGrids.value / totalGrids.value) * 100)
        ElMessage.error(`宫格图${gi + 1}提交失败: ${error.message}`)
        continue
      }

      if (!submitSuccess || !storyboardId) continue

      generationStatus.value = 'generating'
      const gridCompleted = await pollSingleStoryboard(storyboardId, gi)
      if (!gridCompleted) {
        hasFailure = true
      }
      completedGrids.value++
      generationProgress.value = Math.round((completedGrids.value / totalGrids.value) * 100)
    }

    if (hasFailure) {
      generationStatus.value = 'failed'
    } else {
      generationStatus.value = 'completed'
    }
    generationProgress.value = 100
    await loadStoryboards()
  } catch (error) {
    generationStatus.value = 'failed'
    generationProgress.value = 100
    ElMessage.error('生成过程出错: ' + error.message)
  } finally {
    generating.value = false
  }
}

const pollSingleStoryboard = (storyboardId, gridIndex) => {
  return new Promise((resolve) => {
    let pollCount = 0
    const maxPollCount = 120

    const checkStatus = async () => {
      pollCount++
      if (pollCount > maxPollCount) {
        ElMessage.warning(`宫格图${gridIndex + 1}检查超时，后台仍在生成中`)
        resolve(false)
        return
      }

      try {
        const data = await api.get(`/storyboards/${storyboardId}/status`)
        if (data.success) {
          if (data.data.status === 'completed') {
            resolve(true)
            return
          } else if (data.data.status === 'failed') {
            const failMsg = data.data.message || '未知错误'
            if (failMsg.includes('Risk') || failMsg.includes('审核')) {
              ElMessage.error(`宫格图${gridIndex + 1}内容审核未通过，请修改提示词中的敏感内容后重试`)
            } else {
              ElMessage.error(`宫格图${gridIndex + 1}生成失败: ${failMsg}`)
            }
            resolve(false)
            return
          }
        }
      } catch (error) {
        console.error(`[Storyboard] 检查宫格图${gridIndex + 1}状态失败:`, error)
      }

      pollingTimer.value = setTimeout(checkStatus, 5000)
    }

    pollingTimer.value = setTimeout(checkStatus, 5000)
  })
}

const closeAndRefresh = async () => {
  confirmDialogVisible.value = false
  generationStatus.value = 'idle'
  generationProgress.value = 0
  completedGrids.value = 0
  totalGrids.value = 0
  pollingStoryboardIds.value = []
  if (pollingTimer.value) {
    clearTimeout(pollingTimer.value)
    pollingTimer.value = null
  }
  await loadStoryboards()
}

const handleDialogClose = (done) => {
  if (generationStatus.value === 'submitting' || generationStatus.value === 'generating') {
    ElMessageBox.confirm(
      '宫格大图正在生成中，关闭后已提交的任务仍会在后台继续生成。确定要关闭吗？',
      '提示',
      { confirmButtonText: '确定关闭', cancelButtonText: '继续等待', type: 'warning' }
    ).then(() => {
      generationCancelled.value = true
      if (pollingTimer.value) {
        clearTimeout(pollingTimer.value)
        pollingTimer.value = null
      }
      generationStatus.value = 'idle'
      generationProgress.value = 0
      completedGrids.value = 0
      totalGrids.value = 0
      pollingStoryboardIds.value = []
      loadStoryboards()
      done()
    }).catch(() => {})
    return
  }
  if (generationStatus.value === 'completed' || generationStatus.value === 'failed') {
    closeAndRefresh()
    done()
    return
  }
  done()
}

const loadStoryboards = async () => {
  try {
    const data = await api.get(`/projects/${props.projectId}/storyboards`)
    if (data.success) {
      storyboards.value = data.data
    }
  } catch (error) {
    console.error('加载宫格大图失败:', error)
  }
}

const splitStoryboard = async (storyboard) => {
  splittingId.value = storyboard.id
  try {
    const data = await api.post(`/storyboards/${storyboard.id}/split`)
    
    if (data.success) {
      ElMessage.success(`成功切割为${data.data.cells_count}张宫格小图`)
      await loadCells(storyboard.id)
    } else {
      ElMessage.error(data.message || '切割失败')
    }
  } catch (error) {
    ElMessage.error('切割失败: ' + error.message)
  } finally {
    splittingId.value = null
  }
}

const loadCells = async (storyboardId) => {
  try {
    const data = await api.get(`/storyboards/${storyboardId}/cells`)
    if (data.success) {
      cells.value = data.data
    }
  } catch (error) {
    console.error('加载宫格小图失败:', error)
  }
}

const setDefaultStoryboard = async (storyboardId) => {
  try {
    const data = await api.post(`/storyboards/${storyboardId}/default`)
    
    if (data.success) {
      ElMessage.success('已设为默认')
      await loadStoryboards()
    } else {
      ElMessage.error(data.message || '设置失败')
    }
  } catch (error) {
    ElMessage.error('设置失败: ' + error.message)
  }
}

const showOutpaintDialog = () => {
  outpaintDialogVisible.value = true
}

const startOutpaint = async () => {
  if (cells.value.length === 0) {
    ElMessage.warning('请先拆分宫格大图')
    return
  }

  outpainting.value = true
  outpaintDialogVisible.value = false

  const unexpandedCells = cells.value.filter(c => !c.is_expanded)
  if (unexpandedCells.length === 0) {
    ElMessage.info('所有宫格小图已完成扩图')
    outpainting.value = false
    return
  }

  ElMessage.info(`开始批量扩图，共${unexpandedCells.length}张...`)

  let successCount = 0
  let failCount = 0

  for (const cell of unexpandedCells) {
    try {
      outpaintingCellIds.value.add(cell.id)
      const data = await api.post(`/cells/${cell.id}/outpaint`, {
        aspect_ratio: outpaintForm.value.aspectRatio
      })
      
      if (data.success) {
        successCount++
        if (data.data.task_id) {
          startPollingOutpaintStatus(cell.id, data.data.task_id)
        }
      } else {
        failCount++
        outpaintingCellIds.value.delete(cell.id)
      }
    } catch (error) {
      failCount++
      outpaintingCellIds.value.delete(cell.id)
    }
  }

  ElMessage.success(`已提交${successCount}张扩图任务${failCount > 0 ? `，${failCount}张失败` : ''}`)
  outpainting.value = false
}

const startPollingOutpaintStatus = (cellId, taskId) => {
  let pollCount = 0
  const maxPollCount = 60

  const checkStatus = async () => {
    pollCount++
    if (pollCount > maxPollCount) {
      outpaintingCellIds.value.delete(cellId)
      return
    }

    try {
      const data = await api.get(`/cells/${cellId}/outpaint/result`)
      if (data.success) {
        if (data.data.status === 'completed') {
          outpaintingCellIds.value.delete(cellId)
          const cellIdx = cells.value.findIndex(c => c.id === cellId)
          if (cellIdx !== -1) {
            cells.value[cellIdx].is_expanded = 1
            if (data.data.image_url) {
              cells.value[cellIdx].outpainted_image_url = data.data.image_url
            }
          }
          return
        } else if (data.data.status === 'failed') {
          outpaintingCellIds.value.delete(cellId)
          return
        }
      }
      const timer = setTimeout(checkStatus, 5000)
      outpaintPollingTimers.value.push(timer)
    } catch (error) {
      const timer = setTimeout(checkStatus, 10000)
      outpaintPollingTimers.value.push(timer)
    }
  }

  const timer = setTimeout(checkStatus, 5000)
  outpaintPollingTimers.value.push(timer)
}

const outpaintSingleCell = async (cell) => {
  try {
    outpaintingCellIds.value.add(cell.id)
    const data = await api.post(`/cells/${cell.id}/outpaint`, {
      aspect_ratio: outpaintForm.value.aspectRatio || '16:9'
    })
    
    if (data.success) {
      ElMessage.success('扩图任务已提交')
      if (data.data.task_id) {
        startPollingOutpaintStatus(cell.id, data.data.task_id)
      }
    } else {
      outpaintingCellIds.value.delete(cell.id)
      ElMessage.error(data.message || '扩图失败')
    }
  } catch (error) {
    outpaintingCellIds.value.delete(cell.id)
    ElMessage.error('扩图失败: ' + error.message)
  }
}

const loadShots = async () => {
  try {
    const data = await api.get(`/projects/${props.projectId}/shots`)
    if (data.success) {
      shots.value = data.data
    }
  } catch (error) {
    console.error('加载分镜画面失败:', error)
  }
}

const generateVideos = async () => {
  generatingVideos.value = true
  try {
    const data = await api.post(`/projects/${props.projectId}/video-segments`, {
      script_id: props.scriptId
    })
    
    if (data.success) {
      ElMessage.success(`已创建${data.data.length}个视频片段`)
      setTimeout(() => loadVideoSegments(), 10000)
    } else {
      ElMessage.error(data.message || '提交失败')
    }
  } catch (error) {
    ElMessage.error('提交失败: ' + error.message)
  } finally {
    generatingVideos.value = false
  }
}

const loadVideoSegments = async () => {
  try {
    const data = await api.get(`/projects/${props.projectId}/video-segments`)
    if (data.success) {
      videoSegments.value = data.data
    }
  } catch (error) {
    console.error('加载视频素材失败:', error)
  }
}

const mergeVideos = async () => {
  merging.value = true
  try {
    const data = await api.post(`/projects/${props.projectId}/videos/merge`)
    
    if (data.success) {
      ElMessage.success('视频合并成功')
    } else {
      ElMessage.error(data.message || '合并失败')
    }
  } catch (error) {
    ElMessage.error('合并失败: ' + error.message)
  } finally {
    merging.value = false
  }
}

const previewImage = (item) => {
  previewImageUrl.value = item.outpainted_image_url || item.image_url || ''
  previewVisible.value = true
}

const formatTime = (time) => {
  return new Date(time).toLocaleString('zh-CN')
}

onMounted(() => {
  checkCollages()
  loadGridCalc()
  loadStoryboards()
  loadShots()
  loadVideoSegments()
})

onUnmounted(() => {
  if (pollingTimer.value) {
    clearTimeout(pollingTimer.value)
    pollingTimer.value = null
  }
  outpaintPollingTimers.value.forEach(timer => clearTimeout(timer))
  outpaintPollingTimers.value = []
})
</script>

<style scoped>
.storyboard-step {
  padding: 20px;
}

.storyboard-card,
.cells-card,
.shots-card,
.videos-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.storyboards-list {
  margin-top: 16px;
}

.storyboard-item {
  margin-bottom: 16px;
}

.storyboard-item.is-default {
  border: 2px solid #409eff;
}

.storyboard-image {
  position: relative;
  cursor: pointer;
}

.storyboard-image .el-image {
  width: 100%;
  height: 200px;
  border-radius: 4px;
}

.default-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #409eff;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.storyboard-info {
  margin-top: 12px;
}

.storyboard-meta {
  font-size: 14px;
  color: #333;
  margin: 0;
}

.storyboard-time {
  font-size: 12px;
  color: #999;
  margin: 4px 0 0;
}

.storyboard-actions {
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
}

.grid-calc-section {
  background: #fafafa;
  padding: 16px;
  border-radius: 8px;
}

.grid-size-selector {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.grid-size-selector .el-alert {
  flex: 1;
  min-width: 200px;
}

.three-column-layout {
  display: flex;
  gap: 16px;
  margin-top: 20px;
  min-height: 400px;
}

.column-card {
  flex: 1;
  min-width: 0;
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.column-card :deep(.el-card__body) {
  flex: 1;
  overflow-y: auto;
}

.cells-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.shots-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.videos-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.cell-item {
  position: relative;
  cursor: pointer;
}

.cell-item .el-image {
  width: 100%;
  height: 80px;
  border-radius: 4px;
}

.cell-index {
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.expanded-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background: #67c23a;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.outpainting-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background: #e6a23c;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.cell-actions {
  position: absolute;
  bottom: 4px;
  right: 4px;
}

.shot-item {
  margin-bottom: 4px;
}

.shot-image {
  position: relative;
  cursor: pointer;
}

.shot-image .el-image {
  width: 100%;
  height: 100px;
  border-radius: 4px;
}

.outpaint-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background: #67c23a;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.shot-index {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
  text-align: center;
}

.video-item {
  margin-bottom: 4px;
}

.video-preview {
  position: relative;
  cursor: pointer;
}

.video-preview .el-image {
  width: 100%;
  height: 80px;
  border-radius: 4px;
}

.video-play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.video-status {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.video-info {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
  text-align: center;
}

.confirm-dialog :deep(.el-dialog__body) {
  max-height: 70vh;
  overflow-y: auto;
  padding-top: 10px;
}

.scene-edit-block {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  background: #fafafa;
}

.scene-edit-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.scene-edit-title {
  font-weight: 600;
  font-size: 14px;
}

.scene-edit-meta {
  font-size: 12px;
  color: #999;
  margin-left: auto;
}

.prompt-editor :deep(.el-textarea__inner) {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 13px;
  line-height: 1.6;
}

.prompt-stats {
  display: flex;
  gap: 20px;
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}

.ref-images-section h4 {
  margin: 0 0 10px;
  font-size: 14px;
}

.ref-images-grid {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.ref-image-item {
  width: 120px;
  text-align: center;
}

.ref-image-item .el-image {
  width: 120px;
  height: 80px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}

.ref-type {
  display: block;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.confirm-dialog-footer {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
}

.engine-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 6px;
}

.engine-label {
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  white-space: nowrap;
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.mb-4 {
  margin-bottom: 16px;
}

.mt-4 {
  margin-top: 16px;
}

.help-text {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.voice-players {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.voice-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: #f5f7fa;
  border-radius: 4px;
}

.voice-speaker {
  font-size: 12px;
  font-weight: 600;
  color: #409eff;
  min-width: 50px;
}

.voice-speaker-tag {
  margin-right: 4px;
}

.voice-audio {
  height: 28px;
  flex: 1;
}

.voice-duration {
  font-size: 11px;
  color: #999;
  min-width: 35px;
}

.preview-dialog :deep(.el-dialog__body) {
  padding: 10px;
}

.preview-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  max-height: 75vh;
  overflow: auto;
}

.preview-full-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}
</style>
